import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { fetchPutDeliveryFeedbackFlags } from '../../fetch/orderFetch';
import { ButtonComponent } from '../../ReusableComponents/Button/ButtonComponent';
import { ReactComponent as HappyFace } from '../../assets/svg/happy_face.svg';
import { ReactComponent as NeutralFace } from '../../assets/svg/neutral_face.svg';
import { ReactComponent as SadFace } from '../../assets/svg/sad_face.svg';
import { PhotoRating } from '../../types/orderTypes';
import { DataBox } from '../../components/DataBox/DataBox';
import dayjs, { Dayjs } from 'dayjs';
const CS_INVALIDATED = 'CS_INVALIDATED';

export function DeliveryRating({
  deliveredAt,
  deliveryToken,
  rating,
  feedback,
  feedbackFlags,
  photoRating,
  refetchData,
}: {
  deliveredAt?: Dayjs;
  deliveryToken: string;
  rating: number;
  feedback?: string;
  feedbackFlags: string[];
  photoRating?: PhotoRating;
  refetchData: () => void;
}): JSX.Element {
  const [isRatingValid, setRatingValidity] = useState<boolean>(true);

  useEffect(() => {
    if (feedbackFlags) {
      setRatingValidity(feedbackFlags.indexOf(CS_INVALIDATED) === -1);
    }
  }, [feedbackFlags]);

  const smileyIcon = (rating: number): any => {
    switch (rating) {
      case 1:
        return <SadFace />;
      case 3:
        return <NeutralFace />;
      case 5:
        return <HappyFace />;
      case 0:
        return <span>N/A</span>;
      default:
        return <span>{rating}</span>;
    }
  };

  const photoRatingToString = (photoRatingValue: string): string => {
    switch (photoRatingValue) {
      case PhotoRating.BAD:
        return 'Bad';
      case PhotoRating.GOOD:
        return 'Good';
      case PhotoRating.VERY_GOOD:
        return 'Very good';
      default:
        return '-';
    }
  };

  const feedbackFlagsToString = (feedbackFlags: string[]): string => {
    if (feedbackFlags.length > 0) {
      return feedbackFlags
        .filter(flag => flag !== CS_INVALIDATED)
        .map(flag => flag.replaceAll('_', ' ').toLowerCase())
        .join(', ');
    }
    return '-';
  };

  const onToggleRatingAsInvalid = (): void => {
    const ratingChangeInfo =
      'Rating can be changed up to 2 days after delivery. ';

    Swal.fire({
      title: `Mark this rating as ${isRatingValid ? 'invalid' : 'valid'}`,
      icon: 'question',
      text: isRatingValid
        ? `${ratingChangeInfo}Marking this recipient rating as invalid will remove this rating from the courier bonus calculation. The changes will not be visible for the recipient.`
        : `${ratingChangeInfo}This rating is currently not used in the courier bonus calculation. Marking it as valid will include it in the courier rating bonus calculation.`,
      showCancelButton: true,
      confirmButtonText: `Mark as ${isRatingValid ? 'invalid' : 'valid'}`,
      cancelButtonText: 'Cancel',
    }).then(async result => {
      if (result.value) {
        const success = await fetchPutDeliveryFeedbackFlags(
          deliveryToken,
          isRatingValid
            ? [...feedbackFlags, CS_INVALIDATED]
            : feedbackFlags.filter(flag => flag !== CS_INVALIDATED)
        );
        if (!success) {
          await Swal.fire({
            title: 'Could not set rating as invalid',
            icon: 'error',
            text: `An error occured when trying to set rating as invalid`,
          });
        } else {
          refetchData();
        }
      }
    });
  };

  const blockRatingChanges = dayjs().isAfter(
    deliveredAt?.add(2, 'day').endOf('day').subtract(1, 'hour')
  );

  return (
    <DataBox title="Customer feedback">
      <div
        className="ratingValidity"
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        {!isRatingValid && (
          <p style={{ color: 'red' }}>
            This rating is not included in the courier bonus
          </p>
        )}
        <ButtonComponent
          variant="outlined"
          size="small"
          color="primary"
          onClick={() => onToggleRatingAsInvalid()}
          style={{ marginLeft: 'auto' }}
          disabled={blockRatingChanges}
        >
          {`Set rating as ${isRatingValid ? 'invalid' : 'valid'}`}
        </ButtonComponent>
      </div>
      <dl className="definitionlist">
        <dt>Rating:</dt>
        <dd>{rating > 0 ? smileyIcon(rating) : '-'}</dd>
        <dt>Feedback:</dt>
        <dd>{feedback ? feedback : '-'}</dd>
        <dt>Feedback reasons:</dt>
        <dd>{feedbackFlagsToString(feedbackFlags)}</dd>
        <dt>Photo rating:</dt>
        <dd>{photoRatingToString(photoRating)}</dd>
      </dl>
    </DataBox>
  );
}
